import React, { useState, useEffect } from "react";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../components/Constant/Notification";
import teamService from "../../components/Services/TeamService";

const useTeam = () => {
  const [teamName, setTeamName] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [teamInfo, setTeamInfo] = useState({
    name: "",
    members: [],
    bod: [],
    md: [],
    teamId: "",
  });
  const [teamCount, setTeamCount] = useState(0);
  const [teamList, setTeamList] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageToken, setPageToken] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [prevPageToken, setPrevPageToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageTokens, setPageTokens] = useState([null]);
  const [cache] = useState(() => new Map());

  const getCacheKey = (page, search) => `${page}_${search}`;

  const getTeamList = async ({
    search = "",
    page = 1,
    direction = "",
    pageToken: customToken = null,
  } = {}) => {
    const cacheKey = getCacheKey(page, search);
    const tokenForPage = customToken ?? pageTokens[page - 1] ?? null;

    if (cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      setTeamList(cachedData.teams);
      setTeamCount(cachedData.maxCount);
      setHasMore(cachedData.hasMore);
      setNextPageToken(cachedData.nextPageToken);
      setPrevPageToken(cachedData.prevPageToken);
      setCurrentPage(page);
      setPageToken(tokenForPage);
      console.log("Data loaded from cache for page:", page, "cacheKey:", cacheKey);
      return;
    }

    try {
      setLoading(true);

      const body = {
        limit: pageSize,
        searchKey: search.trim() || null,
        next: direction === "next" ? tokenForPage : null,
        prev: direction === "prev" ? tokenForPage : null,
        direction,
      };

      const response = await teamService.fetchTeams(body);
      const teams = response.data.teams || [];
      const maxCount = response.data.maxCount || 0;
      const nextToken = response.data.next || null;
      const prevToken = response.data.prev || null;

      const calculatedHasMore = page * pageSize < maxCount;

      const cacheData = {
        teams,
        maxCount,
        hasMore: calculatedHasMore,
        nextPageToken: nextToken,
        prevPageToken: prevToken,
      };

      cache.set(cacheKey, cacheData);
      console.log("Data cached for page:", page, "cacheKey:", cacheKey);

      if (direction === "next" && nextToken && calculatedHasMore) {
        setPageTokens((prev) => {
          const newTokens = [...prev];
          if (newTokens.length === page) {
            newTokens.push(nextToken);
          }
          return newTokens;
        });
      } else if (direction === "previous" && prevToken && page > 1) {
        setPageTokens((prev) => {
          const newTokens = [...prev];
          if (newTokens.length >= page && newTokens[page - 1] !== prevToken) {
            newTokens[page - 1] = prevToken;
          }
          return newTokens;
        });
      }

      setTeamList(teams);
      setTeamCount(maxCount);
      setHasMore(calculatedHasMore);
      setNextPageToken(nextToken);
      setPrevPageToken(prevToken);
      setPageToken(direction === "next" ? nextToken : prevToken);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching team list:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    cache.clear();
    setCurrentPage(1);
    setPageToken(null);
    setPageTokens([null]);
    getTeamList({ search: searchTerm, page: 1 });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (token, direction) => {
    console.log("Page change requested:", { token, direction, currentPage });

    if (direction === "next" && hasMore) {
      const newPage = currentPage + 1;
      console.log("Moving to next page:", newPage, "with token:", nextPageToken);
      getTeamList({
        search: searchTerm,
        pageToken: nextPageToken,
        page: newPage,
        direction: "next",
      });
    } else if (direction === "previous" && currentPage > 1) {
      const newPage = currentPage - 1;
      const prevToken = pageTokens[newPage - 1] || prevPageToken;
      console.log("Moving to previous page:", newPage, "with token:", prevToken);
      getTeamList({
        search: searchTerm,
        pageToken: prevToken,
        page: newPage,
        direction: "prev",
      });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    console.log("Page size changed to:", newPageSize);
    setPageSize(newPageSize);
  };

  const refreshData = () => {
    cache.clear();
    getTeamList({
      search: searchTerm,
      page: currentPage,
      pageToken: pageTokens[currentPage - 1] || null,
    });
  };

  const handleSubmit = async () => {
    if (!teamName || teamName.trim() === "") {
      showErrorToast("Please enter a team name");
      return;
    }

    try {
      setLoading(true);
      const teamData = { name: teamName.trim() };
      const response = await teamService.createTeam(teamData);
      if (response.data) {
        setIsOpen(false);
        setTeamName("");
        if (response.data?.duplicate) {
          showWarningToast(response.data?.message);
        } else {
          showSuccessToast(response?.data?.message);
        }
      }
    } catch (error) {
      console.log(error);
    }finally {
      setLoading(false);
    }
  };

  const getTeamInfo = async (teamId) => {
    try {
      const response = await teamService.getTeamData(teamId);

      if (response.data) {
        setTeamName(response.data.name || "");
        setTeamInfo({
          members: response.data.members || [],
          bod: response.data.BOD || [],
          md: response.data.MD || [],
          teamId: teamId || "",
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdate = async (teamId, teamName) => {

    if (!teamName || teamName.trim() === "") {
      showErrorToast("Please enter a team name");
      return;
    }
   
    const previousTeamName = teamInfo.name;
    if(previousTeamName === teamName.trim()) {
     setIsOpen(false);
      return;
    }
    setLoading(true);
    try {
      const teamData = { name: teamName.toLowerCase().trim() };
      const response = await teamService.updateTeam(teamId, teamData);
      if (response.data ) {
        setIsOpen(false);
        setTeamName("");
        showSuccessToast(response?.data?.message);
      }
    } catch (error) {
      console.log(error);
    }finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cache.clear();
    setCurrentPage(1);
    setPageToken(null);
    setPageTokens([null]);
    getTeamList({ search: searchTerm, page: 1 });
  }, [pageSize]);

  return {
    teamName,
    setTeamName,
    isOpen,
    loading,
    setIsOpen,
    handleSubmit,
    getTeamInfo,
    teamInfo,
    setTeamInfo,
    handleUpdate,
    teamCount,
    teamList,
    pageSize,
    searchTerm,
    pageToken,
    nextPageToken,
    prevPageToken,
    currentPage,
    hasMore,
    loading,
    handleSearchSubmit,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    refreshData,
  };
};

export default useTeam;